import { useMutation, useQueryClient } from "react-query";
import { useAgentsHttp } from "../useAgentsHttp";
import type { AgentRun, TriageRecord } from "../models/run";

// Public surface of /intake. Mirrors handler._route_intake's wrapped
// response shape: triage carries Atlas's routing decision, result
// carries the specialist agent's structured output (decision +
// resolved_files + intake echo).
export interface IntakeOrganization {
  name: string;
  type: string | null;
}

export interface IntakeFileRef {
  upload_id: string;
  kind: string;
}

export interface IntakeRequest {
  project_id: string;
  address?: string;
  ask?: string;
  mpans?: string[];
  organizations?: IntakeOrganization[];
  file_refs?: IntakeFileRef[];
}

export interface UploadInitiateResponse {
  upload_id: string;
  presigned_url: string;
}

interface IntakeResponse {
  triage: TriageRecord;
  result: {
    project_id: string;
    intake: Record<string, unknown>;
    resolved_files: Array<{
      upload_id: string;
      kind: string;
      original_name: string | null;
    }>;
    decision: Record<string, unknown>;
    text: string;
    model_id: string;
    usage: { input_tokens: number; output_tokens: number };
  };
  // The backend's /intake route auto-persists an AgentRun as a
  // side-effect (best-effort try/except). When persistence succeeds
  // the run_id is returned here so the caller can navigate directly
  // to /runs/:projectId/:runId without going through the
  // eventually-consistent /runs/list. null means persistence failed
  // server-side; caller should surface that.
  run_id: string | null;
}

export interface NewProjectResult {
  project_id: string;
  triage: TriageRecord;
  // Direct run_id from /intake response - no /runs/list round-trip.
  // null when the backend failed to persist the run (best-effort);
  // caller decides whether to navigate to /operations or show error.
  run_id: string | null;
}

// inferKind tags an uploaded file so the backend's extractor + agent
// prompts route it correctly. The five doc-type kinds (ppa, g99_form,
// dno_offer, structural_survey, cashflow) get routed via
// extract_document_template (structured-schema extraction); the
// other kinds use the raw-text extractors.
//
// Order matters: more specific patterns first. G99 / G100 forms
// match BEFORE the generic dno_offer / cost-summary patterns; a
// file named "BRS2_G99_offer.pdf" is a G99 form, not an offer
// letter. ``ppa`` similarly matches BEFORE generic 'other'.
const inferKind = (name: string): string => {
  const lower = name.toLowerCase();
  if (/\.(png|jpe?g|gif|webp|heic|heif)$/i.test(lower)) return "site_photo";
  if (/\.(dwg|dxf|rvt|step|iges)$/i.test(lower)) return "cad_layout";
  if (/\.(txt|eml)$/i.test(lower)) return "email_thread";
  // G99 / G100 / G98 application forms - matched FIRST because some
  // operators name files "G99 SSEN BRS2 offer.pdf" where the file is
  // really the application, not the offer letter.
  if (/(^|[^a-z])(g99|g100|g98)([^a-z]|$)/i.test(lower)) return "g99_form";
  // DNO offer letters - "offer", "FAP" (NGED's Firm Access Pack
  // reference), "FGX" (SSEN's reference). After g99 so "G99 offer"
  // wins for g99_form.
  if (/(offer\b|^fap|^fgx|^conn|coe\b|cost.?summary)/i.test(lower))
    return "dno_offer";
  // Other generic DNO correspondence not covered above.
  if (/loa|ssen|nged|connections/i.test(lower)) return "dno_correspondence";
  // Structural survey / roof condition.
  if (/structural|roof.condition|roof.survey/i.test(lower))
    return "structural_survey";
  // PPA / lease / deed / side letter. Side letters are PPA-adjacent
  // and tagged 'ppa' so the template extractor pulls them too -
  // the model can flag genuine non-PPA docs back to the agent.
  if (/ppa|lease|deed|side.letter|power.purchase/i.test(lower)) return "ppa";
  // PV design package.
  if (/design|sld|stringing|mounting/i.test(lower)) return "platform_report";
  // Cashflow / financial model.
  if (/cash.?flow|cashflow|financial.model|^model\b/i.test(lower))
    return "cashflow";
  if (/\.xlsx?$/i.test(lower)) return "cashflow";
  if (/\.docx?$/i.test(lower)) return "other";
  return "other";
};

const guessContentType = (name: string): string => {
  const lower = name.toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".docx"))
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (lower.endsWith(".xlsx"))
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (lower.endsWith(".doc")) return "application/msword";
  if (lower.endsWith(".xls")) return "application/vnd.ms-excel";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".gif")) return "image/gif";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".txt")) return "text/plain";
  if (lower.endsWith(".eml")) return "message/rfc822";
  return "application/octet-stream";
};

export interface IntakeSubmitInput {
  project_id: string;
  address: string;
  ask: string;
  mpans_csv: string; // raw text the operator typed; we parse + clean
  organizations: IntakeOrganization[]; // pre-filtered to non-empty by caller
  files: File[]; // browser File objects from the drag-drop
  onProgress?: (stage: "uploading" | "atlas", current: number, total: number) => void;
}

// Drive the full New Project submit: upload each file via the
// /uploads/initiate -> PUT to S3 -> /uploads/complete trio, then
// call /intake with the assembled body. Long-running (Bedrock
// thinking time, typically 30-120s).
//
// On success the backend auto-persists a new AgentRun. We invalidate
// runs-list + projects-list so the Operations page picks up the new
// row without a manual refresh.
export const useIntakeSubmit = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<NewProjectResult, Error, IntakeSubmitInput>(
    async (input) => {
      const mpans = input.mpans_csv
        .split(",")
        .map((m) => m.trim())
        .filter(Boolean);
      const organizations = input.organizations
        .filter((o) => o.name.trim().length > 0)
        .map((o) => ({
          name: o.name.trim(),
          type: (o.type ?? "").trim() || null,
        }));

      // STEP 1: upload files one-by-one. Sequential keeps the FE
      // simple; the legacy UI does the same. With small bundles
      // (typically 1-10 files) latency is dominated by the agent
      // call anyway.
      const fileRefs: IntakeFileRef[] = [];
      const totalFiles = input.files.length;
      for (let i = 0; i < totalFiles; i++) {
        const f = input.files[i];
        input.onProgress?.("uploading", i + 1, totalFiles);
        const contentType = f.type || guessContentType(f.name);
        const init = await http<UploadInitiateResponse>("/uploads/initiate",
          {
            project_id: input.project_id,
            original_name: f.name,
            content_type: contentType,
          },
        );
        const putResp = await fetch(init.presigned_url, {
          method: "PUT",
          body: f,
          headers: { "content-type": contentType },
        });
        if (!putResp.ok) {
          const detail = await putResp.text();
          throw new Error(
            `S3 PUT failed for ${f.name}: ${putResp.status} ${detail}`,
          );
        }
        await http("/uploads/complete", {
          upload_id: init.upload_id,
        });
        fileRefs.push({
          upload_id: init.upload_id,
          kind: inferKind(f.name),
        });
      }

      // STEP 2: call /intake. The backend triages, dispatches to
      // Engineer or Grid, and auto-persists an AgentRun.
      input.onProgress?.("atlas", 0, 1);
      const findOrg = (t: string) =>
        organizations.find((o) => (o.type ?? "").toLowerCase() === t.toLowerCase())
          ?.name;

      const body = {
        project_id: input.project_id,
        location: input.address
          ? { lat: 51.5, lon: -0.1, address: input.address }
          : { lat: 51.5, lon: -0.1 },
        mpans,
        ask: input.ask || undefined,
        notes:
          [
            input.ask || "",
            organizations.length
              ? "Organisations: " +
                organizations
                  .map((o) => `${o.name}${o.type ? ` (${o.type})` : ""}`)
                  .join("; ")
              : "",
          ]
            .filter(Boolean)
            .join("\n") || undefined,
        organizations,
        platform_inputs: {
          landlord_name:
            findOrg("Landlord") || findOrg("Owner-occupier") || undefined,
          tenant_name: findOrg("Tenant") || undefined,
        },
        file_refs: fileRefs,
      };

      const wrapped = await http<IntakeResponse>("/intake",
        body,
      );

      return {
        project_id: input.project_id,
        triage: wrapped.triage,
        run_id: wrapped.run_id,
      };
    },
    {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["inrange-agents-runs-list"] });
        qc.invalidateQueries({ queryKey: ["inrange-agents-projects-list"] });
      },
    },
  );
};

// Re-export the helper so the route can call it for previewing
// kinds in the UI before submit (e.g. "this PDF will be tagged
// dno_correspondence").
export { inferKind };
export type { AgentRun };
